import { getAccessToken, logout } from './auth';
import type { CostEstimate } from './costEstimates';
import type { RepReport } from './repReports';
import type { SwapStockBookingResult, TicketDetail } from './tickets';

/**
 * Kombinierter Prozessschritt „KV/Rep-Bericht erstellen" für Assona-24H-Austauschfälle.
 * Ein Formular, ein Aufruf, zwei Dokumente — die beiden Einzelprozesse
 * (`costEstimates.ts`, `repReports.ts`) bleiben unverändert bestehen.
 */

/** Eine der drei für 24H zugelassenen Schadensarten. */
export interface SwapDamageType {
  id:              number;
  key:             string;
  description:     string;
  text:            string;
  additional_text: string;
}

/** Ein vorhandenes Schadensbild des Tickets (nur Bild-Extensions). */
export interface SwapDamageImage {
  id:          number;
  description: string;
  extension:   string;
}

/**
 * Vorbelegung aus der Lieferschein-Position des Austauschgeräts. Jedes Feld kann
 * `null` sein — dann sagt `notices`, warum. Nichts davon ist geschätzt.
 */
export interface SwapDocumentPrefill {
  swap_order_item_id: number | null;
  swap_device:        { id: number; description: string } | null;
  swap_condition:     { id: number; description: string } | null;
  swap_imei_sn:       string | null;
  delivery_net_price: number | null;
  surcharge:          number | null;
  repair_costs_net:   number | null;
  repair_costs_gross: number | null;
  insurance_text:     string;
  repair_info:        string;
  notices:            string[];
  /** Reklamation: KV und Bericht gehen fest mit 0,00 € an Assona — der Betrag ist nicht änderbar. */
  complaint_zero_price: boolean;
  damage_types:       SwapDamageType[];
  damage_images:      SwapDamageImage[];
}

export interface SwapDocumentCreatePayload {
  kv_template_id:    number;
  error_description: string;
  insurance_info?:   string;
  repair_info?:      string;
  repair_costs_net:  number;
  /** Nur wirksam, wenn der Server keine eindeutige SWAP-IMEI ermitteln konnte. */
  swap_imei_sn?:     string;
}

/** Ergebnis eines Klicks: beide Dokumente, das frische Ticket und die Lagerbuchung. */
export interface SwapDocumentResult {
  cost_estimate: CostEstimate;
  rep_report:    RepReport;
  ticket:        TicketDetail;
  /**
   * Ergebnis der Lagerbuchungen zum Austauschgeraet. Erledigte Schritte stehen in
   * `skipped` — beim zweiten Dokumentensatz zum selben Ticket ist das der Normalfall
   * und kein Fehler.
   */
  stock_booking: SwapStockBookingResult | null;
}

interface ApiEnvelope<T> {
  result: 'SUCCESS' | 'UNAUTHORIZED' | 'FORBIDDEN' | 'NOT_FOUND' | 'BAD_REQUEST' | 'INTERNAL_ERROR' | 'METHOD_NOT_ALLOWED' | 'CONFLICT';
  reason?: string;
  fields?: string[];
  items?: T[];
  item?:  T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: ApiEnvelope<unknown>['result'] | 'network' | 'parse',
    readonly fields?: string[],
  ) {
    super(message);
  }
}

const BASE = `${window.location.origin}/api`;

async function request<T>(path: string, init: RequestInit = {}): Promise<ApiEnvelope<T>> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Nicht angemeldet.', 401, 'UNAUTHORIZED');
  }

  let resp: Response;
  try {
    resp = await fetch(path, {
      ...init,
      headers: {
        'Accept':        'application/json',
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${token}`,
        ...(init.headers ?? {}),
      },
    });
  } catch (e) {
    throw new ApiError(`Netzwerkfehler: ${(e as Error).message}`, 0, 'network');
  }

  if (resp.status === 401) {
    logout('none');
  }

  let body: ApiEnvelope<T>;
  try {
    body = (await resp.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(`Unlesbare Server-Antwort (${resp.status})`, resp.status, 'parse');
  }

  if (!resp.ok || body.result !== 'SUCCESS') {
    throw new ApiError(body.reason ?? `Server-Fehler ${resp.status}`, resp.status, body.result, body.fields);
  }

  return body;
}

/** Vorbelegung des Formulars. Lesend — sagt nichts darüber, ob gespeichert werden darf. */
export async function getSwapDocumentPrefill(ticketId: number): Promise<SwapDocumentPrefill> {
  const body = await request<SwapDocumentPrefill>(`${BASE}/tickets/${ticketId}/swap-documents/prefill`, { method: 'GET' });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

/**
 * Legt KV und Reparaturbericht an, setzt den Status und reiht die Assona-Meldung ein.
 * Nur im Status „Eingelangt" erlaubt; ein zweiter Aufruf endet in `CONFLICT`.
 */
export async function createSwapDocuments(
  ticketId: number,
  payload: SwapDocumentCreatePayload,
): Promise<SwapDocumentResult> {
  const body = await request<SwapDocumentResult>(`${BASE}/tickets/${ticketId}/swap-documents`, {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}
